import React, { useState, useEffect, useRef, useCallback } from 'react';

interface VideoItem {
  id: string;
  videoUrl: string;
  titleAr: string;
  icon: string;
}

const VIDEOS: VideoItem[] = [
  {
    id: 'sara',
    videoUrl: 'https://live-stream-x42g253i-v3.tad-ai.com/%D9%85%D8%B5%D9%86%D8%B9%20%D8%A7%D9%84%D8%B5%D9%8A%D8%BA%20%D9%85%D8%B3%D8%AC%D9%84%20%D8%A7%D9%84%D8%B4%D8%A7%D8%B4%D8%A9%D9%A2%D9%A0%D9%A2%D9%A6%D9%A0%D9%A9%D9%A0%D9%A8_%D9%A0%D9%A1%D9%A3%D9%A3%D9%A0%D9%A0.mp4',
    titleAr: 'عن سارة',
    icon: '✨'
  },
  {
    id: 'windows',
    videoUrl: 'https://live-stream-x42g253i-v3.tad-ai.com/%D9%85%D8%B5%D9%86%D8%B9%20%D8%A7%D9%84%D8%B5%D9%8A%D8%BA%20%D9%85%D8%B3%D8%AC%D9%84%20%D8%A7%D9%84%D8%B4%D8%A7%D8%B4%D8%A9%D9%A2%D9%A0%D9%A2%D9%A6%D9%A0%D9%A9%D9%A0%D9%A6_%D9%A0%D9%A2%D9%A3%D9%A2%D9%A3%D9%A6.mp4',
    titleAr: 'تطبيق الويندوز',
    icon: '💻'
  },
  {
    id: 'dashboard',
    videoUrl: 'https://live-stream-x42g253i-v3.tad-ai.com/%D9%85%D8%B5%D9%86%D8%B9%20%D8%A7%D9%84%D8%B5%D9%8A%D8%BA%20%D9%85%D8%B3%D8%AC%D9%84%20%D8%A7%D9%84%D8%B4%D8%A7%D8%B4%D8%A9%D9%A2%D9%A0%D9%A2%D9%A6%D9%A0%D9%A9%D9%A0%D9%A5_%D9%A1%D9%A2%D9%A4%D9%A1%D9%A0%D9%A4_YouTube.mp4',
    titleAr: 'شرح الداشبورد',
    icon: '📊'
  }
];

export const HeroVideoPlayer: React.FC = () => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState<boolean>(true);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const activeTabRef = useRef<number>(0);

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  const playNextVideo = useCallback(() => {
    const next = (activeTabRef.current + 1) % VIDEOS.length;
    activeTabRef.current = next;
    setActiveTab(next);
    setIsPlaying(true);
  }, []);

  const handleTabChange = (index: number) => {
    activeTabRef.current = index;
    setActiveTab(index);
    setIsPlaying(true);
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {
        // Fallback if browser requires mute for initial play
        if (videoRef.current) {
          videoRef.current.muted = true;
          setIsMuted(true);
          videoRef.current.play().catch(() => {});
        }
      });
    }
  };

  // Attempt initial playback immediately on mount
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {
        // If unmuted autoplay blocked by browser policy, start muted and allow one-click unmute
        if (videoRef.current) {
          videoRef.current.muted = true;
          setIsMuted(true);
          videoRef.current.play().catch(() => {});
        }
      });
    }

    // Auto-unmute on first user touch anywhere
    const handleFirstInteraction = () => {
      if (videoRef.current) {
        videoRef.current.muted = false;
        setIsMuted(false);
      }
      window.removeEventListener('click', handleFirstInteraction);
      window.removeEventListener('touchstart', handleFirstInteraction);
      window.removeEventListener('scroll', handleFirstInteraction);
      window.removeEventListener('keydown', handleFirstInteraction);
    };

    window.addEventListener('click', handleFirstInteraction, { passive: true, once: true });
    window.addEventListener('touchstart', handleFirstInteraction, { passive: true, once: true });
    window.addEventListener('scroll', handleFirstInteraction, { passive: true, once: true });
    window.addEventListener('keydown', handleFirstInteraction, { passive: true, once: true });

    return () => {
      window.removeEventListener('click', handleFirstInteraction);
      window.removeEventListener('touchstart', handleFirstInteraction);
      window.removeEventListener('scroll', handleFirstInteraction);
      window.removeEventListener('keydown', handleFirstInteraction);
    };
  }, [activeTab]);

  const togglePlayPause = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!videoRef.current) return;

    if (videoRef.current.paused) {
      videoRef.current.play();
      setIsPlaying(true);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  const toggleMute = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!videoRef.current) return;

    videoRef.current.muted = !videoRef.current.muted;
    setIsMuted(videoRef.current.muted);
  };

  const toggleFullscreen = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!videoRef.current) return;

    if (!document.fullscreenElement) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen().catch(() => {});
      } else if ((videoRef.current as any).webkitRequestFullscreen) {
        (videoRef.current as any).webkitRequestFullscreen();
      } else if ((videoRef.current as any).msRequestFullscreen) {
        (videoRef.current as any).msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      }
    }
  };

  const currentVideo = VIDEOS[activeTab];

  return (
    <div className="w-full max-w-5xl mx-auto my-1 relative z-20 px-0 sm:px-1">
      <div className="relative group rounded-2xl md:rounded-3xl p-1 bg-gradient-to-b from-purple-500/40 via-indigo-500/20 to-emerald-500/30 shadow-[0_0_50px_rgba(139,92,246,0.3)]">
        <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 via-indigo-600 to-emerald-500 rounded-3xl blur-xl opacity-35 group-hover:opacity-60 transition duration-700 -z-10"></div>
        <div className="relative w-full rounded-xl md:rounded-2xl overflow-hidden bg-[#0A0E1A] shadow-2xl flex flex-col">

          {/* SLIDE TABS */}
          <div className="w-full bg-[#070C17] border-b border-white/10 p-1 sm:p-1.5 flex items-stretch gap-1 sm:gap-1.5">
            {VIDEOS.map((vid, idx) => {
              const isActive = activeTab === idx;
              return (
                <button
                  key={vid.id}
                  id={`tad-tab-btn-${idx}`}
                  onClick={() => handleTabChange(idx)}
                  className={`flex-1 py-2 sm:py-2.5 px-2 rounded-lg sm:rounded-xl flex items-center justify-center gap-1.5 sm:gap-2 transition-all duration-300 select-none ${
                    isActive
                      ? 'bg-gradient-to-r from-purple-600/90 to-indigo-600/90 text-white border border-purple-400/40 shadow-md shadow-purple-900/50 scale-[1.01]'
                      : 'bg-white/[0.03] text-gray-400 hover:text-white hover:bg-white/[0.07] border border-transparent'
                  }`}
                >
                  <span className="text-sm sm:text-base">{vid.icon}</span>
                  <span className="text-[11px] sm:text-xs md:text-sm font-bold truncate">{vid.titleAr}</span>
                  {isActive && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse hidden sm:block"></span>}
                </button>
              );
            })}
          </div>

          {/* VIDEO SCREEN */}
          <div className="relative w-full aspect-video bg-black overflow-hidden">
            <video
              ref={videoRef}
              key={currentVideo.videoUrl}
              src={currentVideo.videoUrl}
              autoPlay
              playsInline
              muted={isMuted}
              preload="auto"
              onEnded={playNextVideo}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              className="w-full h-full object-contain"
            />

            {/* Click overlay for play/pause */}
            <div
              onClick={() => togglePlayPause()}
              className="absolute inset-0 z-20 cursor-pointer flex items-center justify-center bg-transparent hover:bg-black/5 transition-colors"
            >
              {!isPlaying && (
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-purple-600/90 text-white flex items-center justify-center shadow-2xl animate-pulse">
                  <svg className="w-8 h-8 sm:w-10 sm:h-10 fill-current translate-x-1" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                </div>
              )}
            </div>

            {/* Top bar */}
            <div className="absolute top-0 inset-x-0 p-3 sm:p-4 bg-gradient-to-b from-black/80 via-black/20 to-transparent z-30 pointer-events-none flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping"></span>
                <span className="text-white font-bold text-xs sm:text-sm">{currentVideo.titleAr}</span>
                <span className="hidden sm:inline-block text-[10px] text-purple-300 bg-purple-900/60 border border-purple-500/30 px-2 py-0.5 rounded-md">
                  تشغيل متتابع 🔁
                </span>
              </div>
              {isMuted && (
                <button
                  onClick={(e) => toggleMute(e)}
                  className="pointer-events-auto flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white text-[11px] sm:text-xs font-bold shadow-lg animate-bounce"
                >
                  <span>🔊</span><span>اضغط لتشغيل الصوت</span>
                </button>
              )}
            </div>

            {/* Bottom controls */}
            <div className="absolute bottom-0 inset-x-0 p-2.5 sm:p-3.5 bg-gradient-to-t from-black/90 via-black/40 to-transparent z-30 flex items-center justify-between pointer-events-auto">
              <div className="flex items-center gap-2">
                <button onClick={(e) => togglePlayPause(e)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-bold backdrop-blur-md border border-white/15 transition-all hover:scale-105 active:scale-95">
                  {isPlaying
                    ? <><svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" /></svg><span>إيقاف</span></>
                    : <><svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg><span>تشغيل</span></>
                  }
                </button>
                <button onClick={(e) => toggleMute(e)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold backdrop-blur-md border transition-all hover:scale-105 active:scale-95 ${isMuted ? 'bg-amber-500/25 text-amber-300 border-amber-500/40' : 'bg-white/10 text-white border-white/15 hover:bg-white/20'}`}>
                  {isMuted ? <><span>🔇</span><span className="hidden sm:inline">الصوت مكتوم</span></> : <><span>🔊</span><span className="hidden sm:inline">الصوت شغال</span></>}
                </button>
                <button onClick={() => playNextVideo()} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-semibold backdrop-blur-md border border-white/15 transition-all hover:scale-105 active:scale-95" title="الفيديو التالي">
                  <span>⏭️</span><span className="hidden md:inline">التالي</span>
                </button>
                <button onClick={(e) => toggleFullscreen(e)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-semibold backdrop-blur-md border border-white/15 transition-all hover:scale-105 active:scale-95" title="تكبير الشاشة">
                  <span>⛶</span><span className="hidden sm:inline">تكبير الشاشة</span>
                </button>
              </div>
              <div className="flex items-center gap-1.5 text-[10px] sm:text-xs text-white/70 bg-black/40 px-2.5 py-1 rounded-lg border border-white/10 backdrop-blur-sm select-none">
                <span className="text-purple-400 font-extrabold">TAD-AI</span>
                <span className="hidden sm:inline">• ذكاء اصطناعي لتداول الأسهم</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroVideoPlayer;